import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Pagination,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  TablePagination,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { faqApi } from '../../api/faqApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const FaqPage = () => {
  const location = useLocation();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFaq, setDeletingFaq] = useState(null);
  
  // 폼 상태
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    keywords: '',
    category: '',
    priority: 0,
    is_active: true,
    answer_mode: 'direct'
  });
  
  // 검색 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // 페이지네이션 상태
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  
  // 필터 상태
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  
  // 알림 상태
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // 에디터 모드: 'wysiwyg' | 'html'
  const [editorMode, setEditorMode] = useState('wysiwyg');

  useEffect(() => {
    loadFaqs();
    loadCategories();
  }, [page, pageSize, categoryFilter, activeFilter]);

  // 채팅 기록에서 전달된 데이터 처리
  useEffect(() => {
    if (location.state?.fromChatHistory && location.state?.chatData) {
      const { chatData } = location.state;
      handleDialogOpen(null, chatData);
      showSnackbar('질의응답 내역에서 데이터를 불러왔습니다. 내용을 확인하고 수정하여 큐레이션된 답변을 생성하세요.', 'info');
      // 브라우저 히스토리를 정리하여 뒤로가기 시 상태가 유지되지 않도록 함
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadFaqs = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize,
        ...(categoryFilter && { category: categoryFilter }),
        ...(activeFilter !== null && { is_active: activeFilter })
      };
      
      const response = await faqApi.listFaqs(params);
      setFaqs(response.items);
      setTotal(response.total);
    } catch (error) {
      showSnackbar('큐레이션된 답변 목록을 불러오는데 실패했습니다.', 'error');
      console.error('큐레이션된 답변 목록 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await faqApi.getCategories();
      setCategories(response);
    } catch (error) {
      console.error('카테고리 로드 오류:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDialogOpen = (faq = null, chatData = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        keywords: faq.keywords || '',
        category: faq.category || '',
        priority: faq.priority,
        is_active: faq.is_active,
        answer_mode: faq.answer_mode || 'direct'
      });
    } else if (chatData) {
      // 채팅 기록에서 전달된 데이터로 폼 초기화
      setEditingFaq(null);
      setFormData({
        question: chatData.question || '',
        answer: chatData.answer || '',
        keywords: chatData.keywords || '',
        category: chatData.category || '',
        priority: 0,
        is_active: true,
        answer_mode: 'direct'
      });
    } else {
      setEditingFaq(null);
      setFormData({
        question: '',
        answer: '',
        keywords: '',
        category: '',
        priority: 0,
        is_active: true,
        answer_mode: 'direct'
      });
    }
    setEditorMode('wysiwyg');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      keywords: '',
      category: '',
      priority: 0,
      is_active: true,
      answer_mode: 'direct'
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.question.trim() || !formData.answer.trim()) {
        showSnackbar('질문과 답변을 모두 입력해주세요.', 'warning');
        return;
      }

      setLoading(true);
      
      if (editingFaq) {
        await faqApi.updateFaq(editingFaq.id, formData);
        showSnackbar('큐레이션된 답변이 수정되었습니다.', 'success');
      } else {
        await faqApi.createFaq(formData);
        showSnackbar('큐레이션된 답변이 생성되었습니다.', 'success');
      }
      
      handleDialogClose();
      loadFaqs();
      loadCategories(); // 새 카테고리가 추가될 수 있으므로
    } catch (error) {
      showSnackbar(
        editingFaq ? '큐레이션된 답변 수정에 실패했습니다.' : '큐레이션된 답변 생성에 실패했습니다.',
        'error'
      );
      console.error('큐레이션된 답변 저장 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await faqApi.deleteFaq(deletingFaq.id);
      showSnackbar('큐레이션된 답변이 삭제되었습니다.', 'success');
      setDeleteDialogOpen(false);
      setDeletingFaq(null);
      loadFaqs();
    } catch (error) {
      showSnackbar('큐레이션된 답변 삭제에 실패했습니다.', 'error');
      console.error('큐레이션된 답변 삭제 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showSnackbar('검색어를 입력해주세요.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await faqApi.searchFaqs({
        query: searchQuery,
        similarity_threshold: 0.7,
        limit: 10
      });
      setSearchResults(response);
    } catch (error) {
      showSnackbar('검색에 실패했습니다.', 'error');
      console.error('큐레이션된 답변 검색 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await faqApi.updateFaq(faq.id, { is_active: !faq.is_active });
      showSnackbar(`큐레이션된 답변이 ${!faq.is_active ? '활성화' : '비활성화'}되었습니다.`, 'success');
      loadFaqs();
    } catch (error) {
      showSnackbar('큐레이션된 답변 상태 변경에 실패했습니다.', 'error');
      console.error('큐레이션된 답변 상태 변경 오류:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // UTC 시간을 한국 시간(UTC+9)으로 변환
    const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    return koreanTime.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1">
        큐레이션된 답변 관리
      </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setSearchDialogOpen(true)}
          >
            큐레이션된 답변 검색 테스트
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen()}
          >
            큐레이션된 답변 추가
          </Button>
        </Box>
      </Box>

      {/* 필터 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>카테고리</InputLabel>
                <Select
                  value={categoryFilter}
                  label="카테고리"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">전체</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>활성화 상태</InputLabel>
                <Select
                  value={activeFilter === null ? '' : activeFilter}
                  label="활성화 상태"
                  onChange={(e) => setActiveFilter(e.target.value === '' ? null : e.target.value)}
                >
                  <MenuItem value="">전체</MenuItem>
                  <MenuItem value={true}>활성화</MenuItem>
                  <MenuItem value={false}>비활성화</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={() => {
                  setCategoryFilter('');
                  setActiveFilter(null);
                  setPage(1);
                }}
                fullWidth
              >
                필터 초기화
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 큐레이션된 답변 테이블 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>번호</TableCell>
              <TableCell>질문</TableCell>
              <TableCell>카테고리</TableCell>
              <TableCell>우선순위</TableCell>
              <TableCell>답변모드</TableCell>
              <TableCell>활성화</TableCell>
              <TableCell>사용횟수</TableCell>
              <TableCell>생성일</TableCell>
              <TableCell>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {faqs.map((faq, index) => (
              <TableRow key={faq.id}>
                <TableCell>{total - ((page - 1) * pageSize + index)}</TableCell>
                <TableCell>
                  <Tooltip title={faq.question} arrow>
                    <Typography variant="body2" sx={{ 
                      maxWidth: 200, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {faq.question}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {faq.category && (
                    <Chip label={faq.category} size="small" color="primary" />
                  )}
                </TableCell>
                <TableCell>{faq.priority}</TableCell>
                <TableCell>
                  <Chip 
                    label={faq.answer_mode === 'enhanced' ? 'LLM 향상' : '직접 답변'} 
                    size="small" 
                    color={faq.answer_mode === 'enhanced' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleToggleActive(faq)}
                    color={faq.is_active ? 'success' : 'default'}
                  >
                    {faq.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{faq.usage_count}</TableCell>
                <TableCell>{formatDate(faq.created_at)}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleDialogOpen(faq)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setDeletingFaq(faq);
                      setDeleteDialogOpen(true);
                    }}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={Math.ceil(total / pageSize)}
          page={page}
          onChange={(event, newPage) => setPage(newPage)}
          color="primary"
        />
      </Box>

      {/* 큐레이션된 답변 추가/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFaq ? '큐레이션된 답변 수정' : '큐레이션된 답변 추가'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="질문"
                fullWidth
                multiline
                rows={2}
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={600}>답변 *</Typography>
                <Box>
                  <Button
                    size="small"
                    variant={editorMode === 'wysiwyg' ? 'contained' : 'outlined'}
                    onClick={() => setEditorMode('wysiwyg')}
                    sx={{ mr: 0.5, minWidth: 80 }}
                  >
                    에디터
                  </Button>
                  <Button
                    size="small"
                    variant={editorMode === 'html' ? 'contained' : 'outlined'}
                    onClick={() => setEditorMode('html')}
                    sx={{ minWidth: 80 }}
                  >
                    HTML
                  </Button>
                </Box>
              </Box>
              {editorMode === 'wysiwyg' ? (
                <Box sx={{
                  '& .ql-container': { minHeight: 180, fontSize: '14px' },
                  '& .ql-editor': { minHeight: 180 },
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.answer}
                    onChange={(value) => setFormData({ ...formData, answer: value })}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ color: [] }, { background: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['link', 'clean'],
                      ],
                    }}
                  />
                </Box>
              ) : (
                <TextField
                  label="HTML 코드"
                  fullWidth
                  multiline
                  rows={10}
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="키워드 (쉼표로 구분)"
                fullWidth
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="예: 설치, 사용법, 문제해결"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                예) 라이선스, 라이센스, 정품, 인증, 활성화, 제품키
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="카테고리"
                fullWidth
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="예: 일반, 기술지원, 라이선스"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                예) 라이선스
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="우선순위 (0-100)"
                type="number"
                fullWidth
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 100 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                자주 묻는 질문은 높은 우선순위(80-100) 설정
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>답변 모드</InputLabel>
                <Select
                  value={formData.answer_mode}
                  label="답변 모드"
                  onChange={(e) => setFormData({ ...formData, answer_mode: e.target.value })}
                >
                  <MenuItem value="direct">직접 답변 (관리자 답변 그대로)</MenuItem>
                  <MenuItem value="enhanced">LLM 향상 답변 (풍성한 설명 추가)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                직접: 등록한 답변 그대로 / LLM 향상: 예제나 상세 설명 추가
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="활성화"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>취소</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {editingFaq ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 검색 테스트 다이얼로그 */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>큐레이션된 답변 검색 테스트</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="검색어"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handleSearch} disabled={loading}>
              검색
            </Button>
          </Box>
          
          {searchResults.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                검색 결과 ({searchResults.length}개)
              </Typography>
              {searchResults.map((result, index) => (
                <Card key={result.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                        Q: {result.question}
                      </Typography>
                      <Chip 
                        label={`유사도: ${(result.similarity_score * 100).toFixed(1)}%`}
                        color={result.similarity_score > 0.8 ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      A: {result.answer}
                    </Typography>
                    {result.category && (
                      <Chip label={result.category} size="small" sx={{ mt: 1 }} />
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>큐레이션된 답변 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            정말로 이 큐레이션된 답변을 삭제하시겠습니까?
          </Typography>
          {deletingFaq && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              질문: {deletingFaq.question}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FaqPage; 